/**
 * MenuZonas — puerta de entrada post-login.
 * IMPL-068 (2026-07-16) · Reordenado + renombrado: fuentes de lead primero (Captación, Prospección),
 * luego ejecución (Desarrollo comercial), luego soporte (Administración).
 */
import React from "react";
import { Target, Compass, Settings, LogOut, ChevronRight, Sparkles, ServerCog } from "lucide-react";

export default function MenuZonas({ user, onOpenZona1, onOpenZona2, onOpenConfig, onOpenAdmin, onOpenProspeccion, onOpenSaaS, onLogout }) {
  const esAdmin = ["admin", "super_admin", "superadmin"].includes(user?.role);
  const accesoSaaS = ["saas_consulta", "saas_operaciones", "admin", "super_admin", "superadmin"].includes(user?.role);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      {/* Header verde uniforme */}
      <header className="bg-[#0a6640] px-6 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs text-white/60 uppercase tracking-wider font-medium">MOBILSAFE Fleet</p>
          <h1 className="text-sm font-semibold text-white" data-testid="menu-zonas-user">
            {user?.nombre || user?.email}
          </h1>
        </div>
        <div className="flex items-center gap-2">
        <button
          onClick={() => window.dispatchEvent(new Event('abrir-cambiar-password'))}
          data-testid="menu-zonas-cambiar-password"
          className="text-sm px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/20 hover:bg-white/20"
        >
          Cambiar contraseña
        </button>
        <button
          onClick={onLogout}
          data-testid="menu-zonas-logout"
          className="text-sm px-3 py-1.5 rounded-lg bg-white/10 text-white border border-white/20 hover:bg-white/20"
        >
          Cerrar sesión
        </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-10">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900">¿Dónde trabajas hoy?</h2>
        </div>

        {/* Flujo: 1 Captación (inbound) → 2 Prospección (outbound) → 3 Desarrollo (cliente/oferta) → 4 Admin (soporte) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* 1 · Captación (inbound) */}
          <ZonaCard
            testid="menu-zona1"
            icon={<Target className="w-6 h-6 text-white" />}
            color="from-orange-500 to-red-500"
            ring="ring-orange-200 hover:ring-orange-400"
            titulo="Captación"
            subtitulo="Inbound · Web · CAES · Calculadora"
            descripcion="Leads que llegan a MOBILSAFE. Gestiona leads entrantes, confirma citas y envía informes CAES al cliente potencial."
            entidades={["leads_caes", "simulaciones", "citas", "informes_caes"]}
            onClick={onOpenZona1}
          />
          {/* 2 · Prospección (outbound) */}
          <ZonaCard
            testid="menu-prospeccion"
            icon={<Sparkles className="w-6 h-6 text-white" />}
            color="from-indigo-500 to-blue-600"
            ring="ring-indigo-200 hover:ring-indigo-400"
            titulo="Prospección"
            subtitulo="Outbound · IA · LinkedIn"
            descripcion="Leads que MOBILSAFE encuentra con IA. Contacta antes que la competencia a oportunidades detectadas cada día."
            entidades={["oportunidades", "linkedin", "email", "llamadas"]}
            onClick={onOpenProspeccion}
          />
          {/* 3 · Desarrollo Comercial (cliente/oferta) */}
          <ZonaCard
            testid="menu-zona2"
            icon={<Compass className="w-6 h-6 text-white" />}
            color="from-emerald-500 to-green-500"
            ring="ring-emerald-200 hover:ring-emerald-400"
            titulo="Desarrollo comercial"
            subtitulo="Clientes · Ofertas · Reuniones · Diagnósticos"
            descripcion="Trabaja con tus clientes activos: reuniones consultivas, Constructor de ofertas, historial de versiones e informes internos."
            entidades={["clientes", "reuniones_consultivas", "ofertas"]}
            onClick={onOpenZona2}
          />
          {/* 4 · Operaciones SaaS */}
          {accesoSaaS && (<ZonaCard
            testid="menu-saas"
            icon={<ServerCog className="w-6 h-6 text-white" />}
            color="from-cyan-600 to-teal-600"
            ring="ring-cyan-200 hover:ring-cyan-400"
            titulo="Operaciones SaaS"
            subtitulo="Altas · Despliegue · Suscripciones · Prefactura"
            descripcion="Opera la flota conectada y su facturación recurrente: altas, despliegue, suscripciones, prefactura y control."
            entidades={["clientes_operativos", "dispositivos", "suscripciones", "prefactura"]}
            onClick={onOpenSaaS}
          />)}
          {/* 5 · Administración (soporte) */}
          {esAdmin && (
            <ZonaCard
              testid="menu-admin"
              icon={<Settings className="w-6 h-6 text-white" />}
              color="from-slate-700 to-slate-900"
              ring="ring-slate-300 hover:ring-slate-500"
              titulo="Administración"
              subtitulo="Config · Usuarios · Analytics"
              descripcion="Configura la plataforma, gestiona usuarios y consulta métricas de rendimiento del equipo comercial."
              entidades={["config_zona1", "config_zona2", "usuarios", "analytics"]}
              onClick={onOpenAdmin}
            />
          )}
        </div>

        <p className="text-xs text-slate-500 mt-12 text-center">
          Una entidad vive en una zona, y solo una. Los leads no aparecen en Desarrollo comercial; los clientes no aparecen en Captación.
          El único puente es "Convertir a cliente" (DEC-004 manual).
        </p>
      </main>
    </div>
  );
}

function ZonaCard({ testid, icon, color, ring, titulo, subtitulo, descripcion, entidades, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative overflow-hidden bg-white hover:bg-slate-50 border border-slate-200 ring-2 ${ring} rounded-2xl p-6 text-left transition group shadow-sm hover:shadow-md`}
    >
      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${color} opacity-10 blur-3xl rounded-full`} />
      <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${color} shadow-lg mb-4`}>
        {icon}
      </div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">{subtitulo}</p>
      <h3 className="text-2xl font-bold mb-2 mt-1 text-slate-900">{titulo}</h3>
      <p className="text-sm text-slate-600 leading-relaxed mb-4">{descripcion}</p>
      <div className="flex flex-wrap gap-1.5">
        {entidades.map((e) => (
          <code key={e} className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full text-slate-600">{e}</code>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-1 text-sm font-medium text-slate-700 group-hover:text-slate-900">
        Entrar <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
      </div>
    </button>
  );
}
